import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { RefreshCw, Copy, MessageSquare, Smartphone, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";

type Contact = Tables<"contacts">;

const TONE_LABELS: Record<string, string> = {
  heartfelt: "Heartfelt",
  humorous: "Humorous",
  professional: "Professional",
  custom: "Custom",
};

const LANGUAGES = [
  "English", "繁體中文", "廣東話", "简体中文", "Tagalog",
  "Hindi", "Bahasa Melayu", "Japanese", "Korean",
  "Spanish", "French", "Portuguese",
];

function hasEnrichmentData(contact: Contact): boolean {
  const c = contact as any;
  return !!(
    c.personality ||
    (c.loves && c.loves.length > 0) ||
    (c.dislikes && c.dislikes.length > 0) ||
    c.gift_ideas ||
    c.shared_memories ||
    c.notes
  );
}

interface MessageGeneratorProps {
  contact: Contact;
  occasionType?: OccasionType;
  occasionLabel?: string;
  occasionId?: string;
  onOpenAbout?: () => void;
}

const MessageGenerator = ({ contact, occasionType, occasionLabel, occasionId, onOpenAbout }: MessageGeneratorProps) => {
  const { user } = useAuth();
  const { isPlus, isAtMessageLimit, messageLimit, canUseOccasionType } = useTierGate();
  const config = occasionType ? OCCASION_TYPES[occasionType] : OCCASION_TYPES.birthday;
  const availableTones = config.tones;
  const singleTone = availableTones.length === 1;

  const [tone, setTone] = useState(availableTones[0]);
  const [customTone, setCustomTone] = useState("");
  const [language, setLanguage] = useState("English");
  const [message, setMessage] = useState("");
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallTitle, setPaywallTitle] = useState("You've used your 5 free messages this month");
  const [paywallBenefit, setPaywallBenefit] = useState("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
  const nudgeShownRef = useRef(false);

  useEffect(() => {
    const fetchUsage = async () => {
      setCountLoading(true);
      if (isPlus) { setCountLoading(false); return; }
      if (!user) { setCountLoading(false); return; }
      const monthYear = new Date().toISOString().slice(0, 7);
      const { data } = await supabase
        .from("ai_message_counts")
        .select("count")
        .eq("user_id", user.id)
        .eq("month_year", monthYear)
        .maybeSingle();
      setMonthlyCount(data?.count ?? 0);
      setCountLoading(false);
    };
    fetchUsage();
  }, [isPlus, user]);

  useEffect(() => {
    setTone(availableTones[0]);
    setGenerated(false);
    setMessage("");
    setTranslation(null);
    setShowTranslation(false);
  }, [occasionType]);

  const MONTHLY_LIMIT = messageLimit ?? 5;
  const remaining = MONTHLY_LIMIT - monthlyCount;
  const atLimit = !isPlus && isAtMessageLimit(monthlyCount);

  const allTones = singleTone
    ? availableTones
    : [...availableTones, "custom"];

  const handleToneSelect = (t: string) => {
    if (t === "custom" && !isPlus) {
      setPaywallTitle("Custom tones are a Plus feature");
      setPaywallBenefit("Upgrade to Circle Plus to write messages in any tone you describe.");
      setPaywallOpen(true);
      return;
    }
    setTone(t);
    if (generated) setGenerated(false);
    setTranslation(null);
    setShowTranslation(false);
  };

  const handleLanguageSelect = (l: string) => {
    setLanguage(l);
    if (generated) setGenerated(false);
    setTranslation(null);
    setShowTranslation(false);
  };

  const generate = async () => {
    if (occasionType && !canUseOccasionType(occasionType)) {
      setPaywallTitle("This occasion type requires Circle Plus");
      setPaywallBenefit("Upgrade to Circle Plus to unlock all occasion types.");
      setPaywallOpen(true);
      return;
    }

    if (!isPlus && isAtMessageLimit(monthlyCount)) {
      setPaywallTitle("You've used your 5 free messages this month");
      setPaywallBenefit("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
      setPaywallOpen(true);
      return;
    }

    const isRegen = generated && message;
    setLoading(true);
    if (isRegen) {
      setIsRegenerating(true);
    } else {
      setMessage("");
    }
    setTranslation(null);
    setShowTranslation(false);

    const body: Record<string, any> = {
      name: contact.full_name,
      relationship: contact.relationship,
      notes: contact.notes,
      tone,
      contact_id: contact.id,
      occasion_type: occasionType || "birthday",
      occasion_label: occasionLabel || null,
      occasion_id: occasionId || null,
      language,
    };
    if (tone === "custom" && customTone) {
      body.customTone = customTone;
    }

    let data: any;
    let error: any;
    let errorData: any = null;

    try {
      const result = await Promise.race([
        supabase.functions.invoke("generate-message", { body }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("TIMEOUT")), 15000)
        ),
      ]);
      data = result.data;
      error = result.error;

      // For non-2xx responses, supabase.functions.invoke puts the response in error
      // and data is null. Try to parse the JSON body from the error.
      if (error && !data) {
        try {
          const response = error.context as Response | undefined;
          if (response && typeof response.clone === "function") {
            errorData = await response.clone().json();
          } else if (typeof error.message === "string") {
            const jsonMatch = error.message.match(/\{.*\}$/s);
            if (jsonMatch) errorData = JSON.parse(jsonMatch[0]);
          }
        } catch {
          // Could not parse error body
        }
      }
    } catch (err: any) {
      setLoading(false);
      if (err?.message === "TIMEOUT") {
        toast.error("Something went wrong generating your message. Please try again.");
      } else {
        toast.error("Something went wrong generating your message. Please try again.");
      }
      return;
    }

    setLoading(false);
    setIsRegenerating(false);

    const errorCode = data?.code || errorData?.code;

    if (error || data?.error) {
      if (errorCode === "MESSAGE_LIMIT_REACHED") {
        setMonthlyCount(MONTHLY_LIMIT);
        setPaywallTitle("You've used your 5 free messages this month");
        setPaywallBenefit("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
        setPaywallOpen(true);
        return;
      }
      if (errorCode === "TONE_UPGRADE_REQUIRED" || errorCode === "OCCASION_UPGRADE_REQUIRED") {
        setPaywallTitle(errorCode === "TONE_UPGRADE_REQUIRED" ? "Custom tones are a Plus feature" : "This occasion type requires Circle Plus");
        setPaywallBenefit(errorCode === "TONE_UPGRADE_REQUIRED"
          ? "Upgrade to Circle Plus to write messages in any tone you describe."
          : "Upgrade to Circle Plus to unlock all occasion types.");
        setPaywallOpen(true);
        return;
      }

      // Check for rate limiting (429) — distinguish monthly limit from hourly
      if (errorCode === "MONTHLY_LIMIT_EXCEEDED") {
        toast.error("You've used all 5 message generations for this month. Upgrade to Circle Plus for unlimited messages.");
        setTimeout(() => {
          setPaywallTitle("You've used your 5 free messages this month");
          setPaywallBenefit("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
          setPaywallOpen(true);
        }, 1000);
        return;
      }
      const statusCode = error?.status || data?.statusCode;
      if (statusCode === 429) {
        toast.error("Too many requests. Please try again later.");
        return;
      }

      toast.error("Unable to generate message right now. Please try again.");
      return;
    }

    setMessage(data.message);
    if (data.translation) setTranslation(data.translation);
    setGenerated(true);
    if (!isPlus) setMonthlyCount((c) => c + 1);

    // Update last_active_at on message generated
    if (user) {
      supabase
        .from("profiles")
        .update({ last_active_at: new Date().toISOString() } as any)
        .eq("user_id", user.id)
        .then(() => {});
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(message);
    toast.success("Copied to clipboard!");
  };

  const sendWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const sendSMS = () => {
    window.open(`sms:?body=${encodeURIComponent(message)}`, "_blank");
  };

  const occasionDisplay = occasionLabel
    || (occasionType ? OCCASION_TYPES[occasionType].label : OCCASION_TYPES.birthday.label);

  const showEnrichmentNudge = generated && message && !hasEnrichmentData(contact) && !nudgeShownRef.current && onOpenAbout;
  if (generated && message && !hasEnrichmentData(contact)) {
    nudgeShownRef.current = true;
  }

  const firstName = contact.full_name.split(" ")[0];

  return (
    <div>
      <h2 className="mb-1 font-serif text-xl font-semibold text-foreground">
        Write a message for {contact.full_name}
      </h2>
      <p className="mb-1 text-sm text-muted-foreground">
        Occasion: {occasionDisplay}
      </p>

      {/* Pre-generation enrichment nudge */}
      {!hasEnrichmentData(contact) && onOpenAbout && (
        <button
          onClick={onOpenAbout}
          className="mb-3 w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <span className="underline decoration-accent underline-offset-2">
            Add details about this contact to get a more personal message →
          </span>
        </button>
      )}

      <p className="mb-4 text-sm text-muted-foreground">
        {singleTone
          ? `We'll craft something ${availableTones[0]}.`
          : "Choose a language, tone, and we'll craft something special."}
      </p>

      {/* Language selector */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Language</p>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {LANGUAGES.map((l) => (
            <button
              key={l}
              onClick={() => handleLanguageSelect(l)}
              className={`shrink-0 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                language === l
                  ? "border-accent bg-accent/10 text-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-accent/50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Tone selector */}
      {!singleTone && (
        <div className="mb-2">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Tone</p>
          <div className="flex gap-2 flex-wrap">
            {allTones.map((t) => (
              <button
                key={t}
                onClick={() => handleToneSelect(t)}
                className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                  tone === t
                    ? "border-accent bg-accent/10 text-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-accent/50"
                }`}
              >
                {TONE_LABELS[t] || t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom tone input */}
      {tone === "custom" && isPlus && (
        <div className="mb-4 mt-2">
          <input
            type="text"
            value={customTone}
            onChange={(e) => setCustomTone(e.target.value)}
            maxLength={100}
            placeholder="Describe the tone — e.g. warm and playful, like a close friend"
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none"
          />
          <p className="mt-1 text-right text-xs text-muted-foreground">{customTone.length}/100</p>
        </div>
      )}

      {/* Spacer when no custom tone input */}
      {(tone !== "custom" || !isPlus) && !singleTone && <div className="mb-6" />}

      {/* Free user remaining count */}
      {!isPlus && !countLoading && monthlyCount > 0 && !atLimit && !generated && (
        <p className="text-xs text-muted-foreground text-center mb-4">
          {remaining} free {remaining === 1 ? 'message' : 'messages'} remaining this month
        </p>
      )}

      {!generated && (
        <>
          <Button
            onClick={generate}
            disabled={loading || atLimit || countLoading || (tone === "custom" && !customTone.trim())}
            className="w-full rounded-full"
            size="lg"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Writing…
              </span>
          ) : atLimit ? (
              <span className="font-semibold text-xs">Free messages used this month</span>
            ) : (
              "Generate Message"
            )}
          </Button>
          {atLimit && (
            <button
              onClick={() => {
                setPaywallTitle("You've used your 5 free messages this month");
                setPaywallBenefit("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
                setPaywallOpen(true);
              }}
              className="mt-2 w-full text-center text-xs font-medium hover:underline"
              style={{ color: '#C9A84C' }}
            >
              Upgrade to Circle Plus for unlimited messages →
            </button>
          )}
        </>
      )}

      {generated && message && (
        <div className="space-y-4">
          <div className="relative rounded-2xl border border-border bg-card p-5 shadow-sm">
            {isRegenerating && (
              <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-card/80 backdrop-blur-[1px]">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Regenerating…
                </span>
              </div>
            )}
            <p className="text-sm leading-relaxed text-foreground">{message}</p>

            {/* Translation toggle */}
            {language !== "English" && translation && (
              <div className="mt-3 border-t border-border pt-3">
                <button
                  onClick={() => setShowTranslation(!showTranslation)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showTranslation ? (
                    <>Hide translation <ChevronUp className="h-3 w-3" /></>
                  ) : (
                    <>See English translation <ChevronDown className="h-3 w-3" /></>
                  )}
                </button>
                {showTranslation && (
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground italic">
                    {translation}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Enrichment nudge */}
          {showEnrichmentNudge && (
            <button
              onClick={onOpenAbout}
              className="mt-3 w-full text-center text-xs text-accent hover:underline"
            >
              Add more to make future messages even more personal →
            </button>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={generate}
              disabled={loading || atLimit}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Regenerate
            </Button>
          </div>
          {!isPlus && (
            atLimit ? (
              <button
                onClick={() => {
                  setPaywallTitle("You've used your 5 free messages this month");
                  setPaywallBenefit("Upgrade to Circle Plus for unlimited messages — write for everyone in your Circle, every month.");
                  setPaywallOpen(true);
                }}
                className="w-full text-center text-xs font-medium hover:underline"
                style={{ color: '#C9A84C' }}
              >
                Upgrade to Circle Plus for unlimited messages →
              </button>
            ) : (
              <p className="text-center text-xs text-muted-foreground">
                {remaining} free {remaining === 1 ? 'message' : 'messages'} remaining this month
              </p>
            )
          )}

          <p className="text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Send via
          </p>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={sendWhatsApp}>
              <MessageSquare className="mr-2 h-4 w-4" />
              WhatsApp
            </Button>
            <Button variant="outline" className="flex-1 rounded-full" onClick={sendSMS}>
              <Smartphone className="mr-2 h-4 w-4" />
              SMS
            </Button>
            <Button variant="outline" className="flex-1 rounded-full" onClick={copyToClipboard}>
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </div>
        </div>
      )}

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title={paywallTitle}
        benefit={paywallBenefit}
      />
    </div>
  );
};

export default MessageGenerator;
